import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import minBy from 'lodash/minBy';
import moment from '@g4/moment-utc';
import { hook, withValue } from '@g4/automation-hooks';
import get from 'lodash/get';
import tracking from '@g4/g4-analytics';

import { isDateAfterDate } from '../validation';
import {
  Media,
  ConditionalWrap,
  Layout,
  Box,
} from '../../../../components/Boneyard';
import FlightItemWithAllegiantFirst from './flightsList/FlightItemWithAllegiantFirst';
import FlightItem from './flightsList/FlightItem';
import { RETURNING, DEPARTING } from '../../constants';
import {
  FLIGHTS_LIST,
  NO_FLIGHTS_RESULTS,
} from '../../../../../automationHooks.json';
import { useTranslations } from '../../../../graphql/components/TranslationsProvider';
import { useAuthentication, useSSRQuery } from '../../../../graphql';
import FlightItemWithBundles from './flightsList/FlightItemWithBundles';
import FLIGHTS_PAGE_TRANSLATIONS_QUERY from '../../flightsPageTranslations.query.graphql.js';

const propTypes = {
  isFlightsQueryLoading: PropTypes.bool.isRequired,
  leg: PropTypes.oneOf(['departing', 'returning']).isRequired,
  flights: PropTypes.array.isRequired,
  date: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired,
  selectedFlightOption: PropTypes.object,
  setSelectedFlight: PropTypes.func.isRequired,
  invalidState: PropTypes.object,
  oppositeLegSelection: PropTypes.object,
  shouldAutoSelect: PropTypes.bool.isRequired,
  setShouldAutoSelect: PropTypes.func.isRequired,
  warning: PropTypes.object,
  deactivateWarning: PropTypes.func,
  loyaltyPointsValue: PropTypes.number,
  numberOfPassengers: PropTypes.number,
  isRedemptionEnabledForNavitaire: PropTypes.bool,
  flightSearchCriteria: PropTypes.object,
  googleAnalyticsData: PropTypes.object,
  selectedBundles: PropTypes.object,
  onBundleSelect: PropTypes.func,
  bundleOffers: PropTypes.array,
  allAncillariesList: PropTypes.array,
  isStadium: PropTypes.bool,
};

const TextBox = styled(Box)`
  text-align: center;
`;

const isSameFlight = (flightOption, selectedFlightOption) => {
  if (!flightOption || !flightOption.flight || !selectedFlightOption) {
    return false;
  }

  return selectedFlightOption.id === flightOption.id;
};

const FlightsList = React.memo(
  ({
    isFlightsQueryLoading,
    flights,
    date,
    leg,
    selectedFlightOption,
    setSelectedFlight,
    invalidState,
    oppositeLegSelection,
    shouldAutoSelect,
    setShouldAutoSelect,
    warning,
    deactivateWarning,
    loyaltyPointsValue,
    numberOfPassengers,
    isRedemptionEnabledForNavitaire,
    flightSearchCriteria,
    googleAnalyticsData,
    selectedBundles,
    onBundleSelect,
    bundleOffers,
    allAncillariesList,
    isStadium = false,
  }) => {
    const translations = useTranslations();
    const { isAuthenticated } = useAuthentication();
    const [isFirstTimeLoading, setIsFirstTimeLoading] = useState(true);
    const route =
      flightSearchCriteria.destination + flightSearchCriteria.origin;

    // when flights change, on initial mount,
    // or when the user scrolls, select the cheapest flight
    // For stadium bookings, use FlightItem instead of NewFlightItem

    const flightPlusBundleViewFlag = !isStadium;

    const { data: translationsQueryData } = useSSRQuery(FLIGHTS_PAGE_TRANSLATIONS_QUERY);
    const isAllegiantFirstFlightsAvailable = translationsQueryData?.application?.configurations?.enableAllegiantFirst;

    const getDefaultOpenBundleFlightId = (flightOptions = []) =>
      flightOptions.length === 1 ? flightOptions[0].id : null;

    const [flightBundleInfoOpen, setFlightBundleInfoOpen] = useState(
      getDefaultOpenBundleFlightId(flights)
    );

    // Reset the open bundle panel whenever the date changes (including revisited/cached dates).
    // Without this, the panel stays closed when cached data is returned immediately (no loading cycle).
    useEffect(() => {
      setFlightBundleInfoOpen(getDefaultOpenBundleFlightId(flights));
    }, [date, route, flights]);

    useEffect(() => {
      if (!flights?.length && !isFlightsQueryLoading && isFirstTimeLoading) {
        tracking.trackZeroSearchResults(route);
        setIsFirstTimeLoading(false);
      }
      if (!shouldAutoSelect || !flights?.length) {
        return;
      }
    }, [
      flights,
      leg,
      shouldAutoSelect,
      setSelectedFlight,
      setShouldAutoSelect,
      oppositeLegSelection,
      route,
      isFirstTimeLoading,
      setIsFirstTimeLoading,
      isFlightsQueryLoading,
    ]);

    const isFlightOptionDisabled = flightOption => {
      if (invalidState && invalidState.disable === leg) {
        return isDateAfterDate(
          get(
            leg === RETURNING ? oppositeLegSelection : flightOption,
            'flight.arrivalTime'
          ),
          get(
            leg === RETURNING ? flightOption : oppositeLegSelection,
            'flight.departingTime'
          )
        );
      }

      return false;
    };

    const getLoyaltyDiscountedPrice = flightOption => {
      if (!loyaltyPointsValue || !isAuthenticated) {
        return -1;
      }

      const isFlightSelected = isSameFlight(flightOption, selectedFlightOption);

      const { price } = flightOption;
      const totalPrice = price * numberOfPassengers;

      if (leg === DEPARTING) {
        if (!isFlightSelected) {
          return null;
        }
        return totalPrice > loyaltyPointsValue
          ? (totalPrice - loyaltyPointsValue) / numberOfPassengers
          : 0;
      }

      const departingLegTotalPrice =
        oppositeLegSelection?.price * numberOfPassengers;

      const pointsAmountLeft =
        loyaltyPointsValue > departingLegTotalPrice
          ? loyaltyPointsValue - departingLegTotalPrice
          : 0;

      if (pointsAmountLeft) {
        if (isFlightSelected) {
          return totalPrice > pointsAmountLeft
            ? (totalPrice - pointsAmountLeft) / numberOfPassengers
            : 0;
        }
        return null;
      }

      return -1;
    };

    const showBundlesInfo = (flightOption) => {
      return flightBundleInfoOpen === flightOption.id;
    };

    return (
      <>
        {!flights?.length ? (
          <TextBox
            justifyContent="center"
            d={'flex'}
            p={6}
            automationHook={withValue(NO_FLIGHTS_RESULTS.hook, leg)}
          >
            {translations.flightsPage.noFlightsResultsText}
          </TextBox>
        ) : (
          <Media>
            {mq => (
              <>
                <ConditionalWrap
                  condition={mq.lt('md')}
                  wrap={children => <Layout.FullWidth children={children} />}
                  children={
                    <form
                      onSubmit={e => e.preventDefault()}
                      {...hook(FLIGHTS_LIST.hook, leg)}
                    >
                      {flights.map(flightOption =>
                        flightPlusBundleViewFlag && !isAllegiantFirstFlightsAvailable ? (
                         <FlightItemWithBundles
                            showBundlesInfo={showBundlesInfo(flightOption)}
                            selectedBundles={selectedBundles}
                           selectedFlightOption={selectedFlightOption}
                            onBundleSelect={onBundleSelect}
                            setFlightBundleInfoOpen={(id, shouldToggle = true) => {
                              if (shouldToggle && id === flightBundleInfoOpen) {
                                setFlightBundleInfoOpen(null)
                              } else {
                                setFlightBundleInfoOpen(id);
                              }
                            }}
                            isFlightsQueryLoading={isFlightsQueryLoading}
                            setFlightSelected={(fareName = 'DF') => {
                              setSelectedFlight({
                                ...flightOption,
                                selectedFareName: fareName,
                              });
                            }}
                            selected={isSameFlight(
                              flightOption,
                              selectedFlightOption
                            )}
                            flightOption={flightOption}
                            key={flightOption.id}
                            leg={leg}
                            date={date}
                            position={flights.indexOf(flightOption) + 1}
                            disabled={
                              isFlightOptionDisabled(flightOption) ||
                              (leg === RETURNING &&
                                moment(
                                  flightOption?.flight.departingTime
                                ).isBefore(
                                  moment(
                                    oppositeLegSelection?.flight.arrivalTime
                                  )
                                ))
                            }
                            disableFlightIfSameDayBasedOnTime={
                              leg === RETURNING &&
                              moment(
                                flightOption?.flight.departingTime
                              ).isBefore(
                                moment(oppositeLegSelection?.flight.arrivalTime)
                              )
                            }
                            warning={warning}
                            deactivateWarning={deactivateWarning}
                            loyaltyDiscountedPrice={getLoyaltyDiscountedPrice(
                              flightOption
                            )}
                            providerId={flightOption?.flight?.providerId}
                            isRedemptionEnabledForNavitaire={
                              isRedemptionEnabledForNavitaire
                            }
                            flightSearchCriteria={flightSearchCriteria}
                            googleAnalyticsData={googleAnalyticsData}
                            oppositeLegSelection={oppositeLegSelection}
                            bundleOffers={bundleOffers}
                            allAncillariesList={allAncillariesList}
                          />
                        ) : isAllegiantFirstFlightsAvailable ? (
                          <FlightItemWithAllegiantFirst
                            showBundlesInfo={showBundlesInfo(flightOption)}
                            selectedBundles={selectedBundles}
                           selectedFlightOption={selectedFlightOption}
                            onBundleSelect={onBundleSelect}
                            setFlightBundleInfoOpen={(id, shouldToggle = true) => {
                              if (shouldToggle && id === flightBundleInfoOpen) {
                                setFlightBundleInfoOpen(null)
                              } else {
                                setFlightBundleInfoOpen(id);
                              }
                            }}
                            isFlightsQueryLoading={isFlightsQueryLoading}
                            setFlightSelected={(fareName = 'DF') => {
                              setSelectedFlight({
                                ...flightOption,
                                selectedFareName: fareName,
                              });
                            }}
                            selected={isSameFlight(
                              flightOption,
                              selectedFlightOption
                            )}
                            flightOption={flightOption}
                            key={flightOption.id}
                            leg={leg}
                            date={date}
                            position={flights.indexOf(flightOption) + 1}
                            disabled={
                              isFlightOptionDisabled(flightOption) ||
                              (leg === RETURNING &&
                                moment(
                                  flightOption?.flight.departingTime
                                ).isBefore(
                                  moment(
                                    oppositeLegSelection?.flight.arrivalTime
                                  )
                                ))
                            }
                            disableFlightIfSameDayBasedOnTime={
                              leg === RETURNING &&
                              moment(
                                flightOption?.flight.departingTime
                              ).isBefore(
                                moment(oppositeLegSelection?.flight.arrivalTime)
                              )
                            }
                            warning={warning}
                            deactivateWarning={deactivateWarning}
                            loyaltyDiscountedPrice={getLoyaltyDiscountedPrice(
                              flightOption
                            )}
                            providerId={flightOption?.flight?.providerId}
                            isRedemptionEnabledForNavitaire={
                              isRedemptionEnabledForNavitaire
                            }
                            flightSearchCriteria={flightSearchCriteria}
                            googleAnalyticsData={googleAnalyticsData}
                            oppositeLegSelection={oppositeLegSelection}
                            bundleOffers={bundleOffers}
                            allAncillariesList={allAncillariesList}
                          />
                        ) : (<FlightItem
                            isFlightsQueryLoading={isFlightsQueryLoading}
                            setFlightSelected={(fareName = 'DF') => {
                              setSelectedFlight({
                                ...flightOption,
                                selectedFareName: fareName,
                              });
                            }}
                            selected={isSameFlight(
                              flightOption,
                              selectedFlightOption
                            )}
                            flightOption={flightOption}
                            key={flightOption.id}
                            leg={leg}
                            date={date}
                            position={flights.indexOf(flightOption) + 1}
                            disabled={
                              isFlightOptionDisabled(flightOption) ||
                              (leg === RETURNING &&
                                moment(
                                  flightOption?.flight.departingTime
                                ).isBefore(
                                  moment(
                                    oppositeLegSelection?.flight.arrivalTime
                                  )
                                ))
                            }
                            disableFlightIfSameDayBasedOnTime={
                              leg === RETURNING &&
                              moment(
                                flightOption?.flight.departingTime
                              ).isBefore(
                                moment(oppositeLegSelection?.flight.arrivalTime)
                              )
                            }
                            warning={warning}
                            deactivateWarning={deactivateWarning}
                            loyaltyDiscountedPrice={getLoyaltyDiscountedPrice(
                              flightOption
                            )}
                            providerId={flightOption?.flight?.providerId}
                            isRedemptionEnabledForNavitaire={
                              isRedemptionEnabledForNavitaire
                            }
                            flightSearchCriteria={flightSearchCriteria}
                            googleAnalyticsData={googleAnalyticsData}
                            oppositeLegSelection={oppositeLegSelection}
                          />)
                      )}
                    </form>
                  }
                />
              </>
            )}
          </Media>
        )}
      </>
    );
  }
);
FlightsList.displayName = 'FlightList';
FlightsList.propTypes = propTypes;
// FlightsList.whyDidYouRender = true;

export default withTheme(FlightsList);
 